/*
 * NARENJ Lens Selector — wizard for the Levision Shopify Atelier theme.
 *
 * Mounts as a Web Component <lens-selector-component> next to Atelier's buy-buttons block.
 * Lazy-loads lens-data.json + lens-addon-variant-map.json on first wizard open.
 * Vanilla JS (no framework). ES2022. Targets evergreen browsers.
 *
 * Security note: every dynamic interpolation in the HTML strings below is passed through
 * `escapeHtml()` before insertion. We use `createContextualFragment()` (Range-based parsing)
 * rather than direct `.innerHTML` assignment to signal that intent explicitly.
 *
 * Spec: sites/levision/_spec/execution-plan.md §2.
 */

// URLs come from the component's `data-lens-data-url` and `data-addon-map-url` attributes
// (set by Liquid via {{ 'lens-data.json' | asset_url }}).
const SESSION_KEY = 'narenj-lens-data-v1';

// ----------------- pure helpers (testable in isolation) -----------------

export function computeLensPrice(state, data) {
  if (!state.type) return 0;
  let total = 0;
  // Base (material) price is only added once a material is chosen. Coatings are added
  // independently so the running subtotal reflects coating choices in step 3, before a
  // material is picked. At add-to-cart time the material is always set, so the final
  // lens price (and the add-on variant bracket) is unaffected by this.
  if (state.material) {
    const base = data.basePrice?.[state.type]?.[state.material];
    if (base !== undefined) total += base;
  }
  if (state.coatings.blueLight) total += data.coatings.blueLight.price;
  if (state.coatings.sunglass) {
    const c = data.coatings[state.coatings.sunglass];
    if (c) total += c.price;
  }
  return total;
}

export function roundUpToBracket(price, bracketSize) {
  if (price <= 0) return 0;
  return Math.ceil(price / bracketSize) * bracketSize;
}

export function gidToNumericId(gid) {
  return String(gid).split('/').pop();
}

export function isStepValid(step, state, data) {
  if (step === 1) return state.type !== null;
  if (step === 2) {
    if (state.rx.sendLater) return true;
    // A prescription without sphere is unusable, so require SPH for both eyes.
    // (Plano lenses still pick an explicit "+0.00", so this never blocks them.)
    if (!state.rx.od.sph || !state.rx.os.sph) return false;
    const pd = state.rx.pd;
    if (pd.mode === 'single') return !!pd.single;
    return !!pd.rightPd && !!pd.leftPd;
  }
  if (step === 3) {
    if (!state.coatings.sunglass) return true;
    return state.coatings.sunglassColor !== null;
  }
  if (step === 4) {
    if (!state.material) return false;
    return data.basePrice?.[state.type]?.[state.material] !== undefined;
  }
  if (step === 5) return true;
  return false;
}

export function describeRxLine(eye, rx, typeReqAdd) {
  const parts = [`SPH ${rx.sph || '-'}`, `CYL ${rx.cyl || '-'}`, `AXIS ${rx.axis || '-'}`];
  if (typeReqAdd) parts.push(`ADD ${rx.add || '-'}`);
  return `${eye}: ${parts.join(' · ')}`;
}

export function describePD(pd) {
  if (pd.mode === 'single') return pd.single ? `${pd.single} mm` : '-';
  if (pd.rightPd && pd.leftPd) return `R ${pd.rightPd} / L ${pd.leftPd}`;
  return '-';
}

export function describeCoatings(coatings, data) {
  const parts = [];
  if (coatings.blueLight) parts.push(data.coatings.blueLight.label);
  if (coatings.sunglass) {
    const c = data.coatings[coatings.sunglass];
    const color = coatings.sunglassColor;
    parts.push(color ? `${c.label} (${capitalize(color)})` : c.label);
  }
  return parts.length ? parts.join(', ') : '-';
}

function capitalize(s) { return s ? s[0].toUpperCase() + s.slice(1) : s; }

export function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

// ----------------- safe DOM helpers -----------------

function escapeHtml(s) {
  if (s === null || s === undefined) return '';
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

// Parse a trusted HTML string (with all interpolations pre-escaped above) into a DocumentFragment.
// We use this instead of `el.innerHTML = ...` to signal that interpolations are already safe.
function html(template) {
  return document.createRange().createContextualFragment(template);
}

function setBody(el, template) {
  el.replaceChildren(html(template));
}

// ----------------- data loading -----------------

async function loadData(dataUrl, addonUrl) {
  try {
    const cached = sessionStorage.getItem(SESSION_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed.data && parsed.addon) return parsed;
    }
  } catch {}

  const [dataRes, addonRes] = await Promise.all([fetch(dataUrl), fetch(addonUrl)]);
  if (!dataRes.ok) throw new Error(`lens-data.json: ${dataRes.status}`);
  if (!addonRes.ok) throw new Error(`lens-addon-variant-map.json: ${addonRes.status}`);
  const data = await dataRes.json();
  const addon = await addonRes.json();
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify({ data, addon })); } catch {}
  return { data, addon };
}

// ----------------- state -----------------

function initialState() {
  return {
    step: 1,
    type: null,
    rx: {
      od: { sph: '', cyl: '', axis: '', add: '' },
      os: { sph: '', cyl: '', axis: '', add: '' },
      pd: { mode: 'single', single: '', rightPd: '', leftPd: '' },
      sendLater: false,
    },
    coatings: { blueLight: false, sunglass: null, sunglassColor: null },
    material: null,
    patientName: '',
  };
}

// ----------------- range helpers -----------------

function rangeValues({ min, max, step }) {
  const out = [];
  const decimals = step.toString().includes('.') ? step.toString().split('.')[1].length : 0;
  for (let v = min; v <= max + 1e-9; v += step) out.push(Number(v.toFixed(decimals)));
  return out;
}

function signedFmt(v, decimals = 2) {
  const n = Number(v);
  return (n >= 0 ? '+' : '') + n.toFixed(decimals);
}

function optsHtml(values, formatter, currentValue) {
  const out = ['<option value="">--</option>'];
  for (const v of values) {
    const valStr = formatter ? formatter(v).valueAttr : String(v);
    const label = formatter ? formatter(v).label : String(v);
    const sel = String(currentValue) === valStr ? ' selected' : '';
    out.push(`<option value="${escapeHtml(valStr)}"${sel}>${escapeHtml(label)}</option>`);
  }
  return out.join('');
}

// ----------------- component -----------------

class LensSelectorComponent extends HTMLElement {
  constructor() {
    super();
    this.state = initialState();
    this.data = null;
    this.addon = null;
    this.dialog = null;
    this.frameVariantId = null;
    this.frameTitle = '';
    this.framePrice = 0;
  }

  connectedCallback() {
    this.frameVariantId = this.dataset.frameVariantId || null;
    this.frameTitle = this.dataset.frameTitle || '';
    this.framePrice = parseInt(this.dataset.framePriceCents || '0', 10);
    this.frameImage = this.dataset.frameImage || '';
    this.frameImageWidth = this.dataset.frameImageWidth || '';
    this.frameImageHeight = this.dataset.frameImageHeight || '';
    this.lensDataUrl = this.dataset.lensDataUrl || '';
    this.addonMapUrl = this.dataset.addonMapUrl || '';

    this.handleOpen = this.handleOpen.bind(this);
    this.handleClose = this.handleClose.bind(this);

    const triggers = document.querySelectorAll('[data-lens-selector-open]');
    triggers.forEach(btn => btn.addEventListener('click', this.handleOpen));

    // Frame-only path — lenses are optional. Adds the frame to cart with no Lens Add-on line.
    this.querySelectorAll('[data-lens-selector-frame-only]').forEach(btn => {
      btn.addEventListener('click', () => this.handleFrameOnly(btn));
    });
  }

  async handleFrameOnly(btn) {
    if (btn.disabled) return;
    btn.disabled = true;
    const originalLabel = btn.textContent;
    btn.textContent = 'Adding…';
    try {
      await this.postToCart([
        { id: this.frameVariantId, quantity: 1, properties: { 'Lenses': 'Frame only, no lenses' } },
      ]);
      this.openCartDrawer();
    } catch (err) {
      console.error('[lens-selector] frame-only cart add failed', err);
      alert(`Sorry, we couldn't add this to your cart. Please try again.`);
    } finally {
      btn.disabled = false;
      btn.textContent = originalLabel;
    }
  }

  async handleOpen(evt) {
    evt?.preventDefault?.();
    try {
      if (!this.data) {
        const loaded = await loadData(this.lensDataUrl, this.addonMapUrl);
        this.data = loaded.data;
        this.addon = loaded.addon;
      }
      this.state = initialState();
      this.renderDialog();
      this.dialog.showModal();
    } catch (err) {
      console.error('[lens-selector] open failed:', err);
      alert('Sorry, the lens selector failed to load. Please refresh and try again.');
    }
  }

  handleClose() { if (this.dialog) this.dialog.close(); }

  renderDialog() {
    if (this.dialog) { this.dialog.remove(); this.dialog = null; }
    const dialog = document.createElement('dialog');
    dialog.className = 'lens-selector__dialog';
    dialog.setAttribute('aria-labelledby', 'lens-selector-step-heading');
    setBody(dialog, this.shellTemplate());
    document.body.appendChild(dialog);
    this.dialog = dialog;
    this.bindDialogEvents();
    this.renderCurrentStep();
  }

  shellTemplate() {
    return `
      <div class="lens-selector__layout">
        <aside class="lens-selector__frame-panel" aria-hidden="true">
          ${this.frameImage ? `<div class="lens-selector__frame-figure">
            <img class="lens-selector__frame-image" src="${escapeHtml(this.frameImage)}" alt=""${this.frameImageWidth ? ` width="${escapeHtml(this.frameImageWidth)}"` : ''}${this.frameImageHeight ? ` height="${escapeHtml(this.frameImageHeight)}"` : ''} loading="lazy" decoding="async">
          </div>` : ''}
          <div class="lens-selector__frame-name">${escapeHtml(this.frameTitle)}</div>
          <div class="lens-selector__subtotal">
            <span class="lens-selector__subtotal-label">Subtotal</span>
            <span class="lens-selector__subtotal-value" data-lens-selector-subtotal>$${(this.framePrice / 100).toFixed(2)}</span>
          </div>
        </aside>
        <section class="lens-selector__main" aria-live="polite">
          <header class="lens-selector__header">
            <button type="button" class="lens-selector__back" data-lens-selector-back aria-label="Previous step" hidden>←</button>
            <div class="lens-selector__progress" aria-hidden="true">
              <div class="lens-selector__progress-bar" data-lens-selector-progress style="--lens-progress: 20%"></div>
            </div>
            <button type="button" class="lens-selector__close" data-lens-selector-close aria-label="Close lens selector">✕</button>
          </header>
          <div class="lens-selector__visually-hidden" aria-live="polite" data-lens-selector-announcer></div>
          <h2 id="lens-selector-step-heading" class="lens-selector__step-heading" data-lens-selector-step-heading></h2>
          <div class="lens-selector__step-body" data-lens-selector-step-body></div>
          <footer class="lens-selector__footer">
            <button type="button" class="lens-selector__continue" data-lens-selector-continue disabled>Continue</button>
          </footer>
        </section>
      </div>`;
  }

  bindDialogEvents() {
    this.dialog.addEventListener('click', (e) => {
      const t = e.target.closest('[data-lens-selector-close], [data-lens-selector-back], [data-lens-selector-continue]');
      if (!t) return;
      if (t.matches('[data-lens-selector-close]')) this.handleClose();
      else if (t.matches('[data-lens-selector-back]')) this.goToStep(this.state.step - 1);
      else if (t.matches('[data-lens-selector-continue]')) this.handleContinue();
    });
  }

  announce(text) {
    const el = this.dialog?.querySelector('[data-lens-selector-announcer]');
    if (el) el.textContent = text;
  }

  goToStep(n) {
    n = Math.max(1, Math.min(5, n));
    this.state.step = n;
    this.renderCurrentStep();
  }

  handleContinue() {
    if (!isStepValid(this.state.step, this.state, this.data)) {
      if (this.state.step === 2 && !this.state.rx.sendLater) {
        this.openRxRescueModal();
        return;
      }
      return;
    }
    if (this.state.step === 5) { this.handleAddToCart(); return; }
    this.goToStep(this.state.step + 1);
  }

  renderCurrentStep() {
    const body = this.dialog.querySelector('[data-lens-selector-step-body]');
    const heading = this.dialog.querySelector('[data-lens-selector-step-heading]');
    const back = this.dialog.querySelector('[data-lens-selector-back]');
    const progress = this.dialog.querySelector('[data-lens-selector-progress]');

    back.hidden = this.state.step === 1;
    progress.style.setProperty('--lens-progress', `${this.state.step * 20}%`);

    let template = '';
    let title = '';
    switch (this.state.step) {
      case 1: title = 'What do you use your glasses for?'; template = this.renderStepType(); break;
      case 2: title = 'Enter your prescription'; template = this.renderStepRx(); break;
      case 3: title = 'Choose your coatings'; template = this.renderStepCoatings(); break;
      case 4: title = 'Choose your lens material'; template = this.renderStepMaterial(); break;
      case 5: title = 'Review your selection'; template = this.renderStepReview(); break;
    }
    heading.textContent = title;
    setBody(body, template);
    this.bindStepEvents();
    this.updateContinueButton();
    this.announce(`Step ${this.state.step} of 5: ${title}`);
    body.querySelector('button, input, select, [tabindex]:not([tabindex="-1"])')?.focus();
  }

  updateContinueButton() {
    const btn = this.dialog.querySelector('[data-lens-selector-continue]');
    const valid = isStepValid(this.state.step, this.state, this.data);
    btn.disabled = !valid && !(this.state.step === 2);
    btn.textContent = this.state.step === 5 ? 'Add to cart' : 'Continue';
    this.updateSubtotal();
  }

  updateSubtotal() {
    const lensPrice = computeLensPrice(this.state, this.data);
    const total = (this.framePrice / 100) + lensPrice;
    const el = this.dialog.querySelector('[data-lens-selector-subtotal]');
    if (el) el.textContent = `$${total.toFixed(2)}`;
  }

  bindStepEvents() {
    const body = this.dialog.querySelector('[data-lens-selector-step-body]');

    body.querySelectorAll('[data-type]').forEach(el => {
      el.addEventListener('click', () => {
        this.state.type = el.dataset.type;
        body.querySelectorAll('[data-type]').forEach(c => c.classList.toggle('is-selected', c === el));
        if (this.state.material && this.data.basePrice[this.state.type]?.[this.state.material] === undefined) {
          this.state.material = null;
        }
        this.updateContinueButton();
      });
    });

    body.querySelectorAll('[data-rx-input]').forEach(el => {
      el.addEventListener('change', () => {
        const [eye, field] = el.dataset.rxInput.split(':');
        if (eye === 'pd') this.state.rx.pd[field] = el.value;
        else this.state.rx[eye][field] = el.value;
        this.updateContinueButton();
      });
    });
    body.querySelector('[data-pd-mode]')?.addEventListener('change', (e) => {
      this.state.rx.pd.mode = e.target.checked ? 'dual' : 'single';
      if (e.target.checked) this.state.rx.pd.single = '';
      else { this.state.rx.pd.rightPd = ''; this.state.rx.pd.leftPd = ''; }
      this.renderCurrentStep();
    });
    body.querySelector('[data-rx-send-later]')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.state.rx.sendLater = true;
      this.goToStep(3);
    });

    body.querySelector('[data-coating-blue]')?.addEventListener('change', (e) => {
      this.state.coatings.blueLight = e.target.checked;
      this.updateContinueButton();
    });
    body.querySelectorAll('[data-coating-sun]').forEach(el => {
      el.addEventListener('change', (e) => {
        const id = e.target.value;
        if (e.target.checked) {
          this.state.coatings.sunglass = id;
          this.state.coatings.sunglassColor = null;
        } else if (this.state.coatings.sunglass === id) {
          this.state.coatings.sunglass = null;
          this.state.coatings.sunglassColor = null;
        }
        this.renderCurrentStep();
      });
    });
    body.querySelectorAll('[data-coating-color]').forEach(el => {
      el.addEventListener('change', (e) => {
        this.state.coatings.sunglassColor = e.target.value;
        this.updateContinueButton();
      });
    });

    body.querySelectorAll('[data-material]').forEach(el => {
      el.addEventListener('click', () => {
        this.state.material = el.dataset.material;
        body.querySelectorAll('[data-material]').forEach(c => c.classList.toggle('is-selected', c === el));
        this.updateContinueButton();
      });
    });

    body.querySelectorAll('[data-jump-to]').forEach(el => {
      el.addEventListener('click', (e) => { e.preventDefault(); this.goToStep(parseInt(el.dataset.jumpTo, 10)); });
    });
    body.querySelector('[data-patient-name]')?.addEventListener('input', (e) => {
      this.state.patientName = e.target.value;
    });
  }

  // ----------------- step renderers (all string interpolations escaped) -----------------

  renderStepType() {
    return this.data.lensTypes.map(t => `
      <button type="button" class="lens-selector__card${this.state.type === t.id ? ' is-selected' : ''}" data-type="${escapeHtml(t.id)}">
        <h3 class="lens-selector__card-title">${escapeHtml(t.label)}</h3>
        <p class="lens-selector__card-desc">${escapeHtml(t.description)}</p>
      </button>`).join('');
  }

  renderStepRx() {
    const r = this.data.rxRanges;
    const typeObj = this.data.lensTypes.find(t => t.id === this.state.type);
    const showAdd = typeObj?.requiresAdd;
    const pd = this.state.rx.pd;
    const dualMode = pd.mode === 'dual';

    const sphFmt = (v) => ({ valueAttr: v.toFixed(2), label: signedFmt(v) });
    // CYL is minus-cylinder notation only (0.00 to -4.00) — no plus sign, 0 renders plain.
    const cylFmt = (v) => ({ valueAttr: v.toFixed(2), label: v.toFixed(2) });
    const axFmt = (v) => ({ valueAttr: String(v), label: String(v) });
    const addFmt = (v) => ({ valueAttr: v.toFixed(2), label: `+${v.toFixed(2)}` });
    const pdFmt = (v) => ({ valueAttr: v.toFixed(1), label: `${v.toFixed(1)} mm` });

    const sphereVals = rangeValues(r.sphere);
    const cylinderVals = rangeValues(r.cylinder);
    const axisVals = rangeValues(r.axis);
    const addVals = rangeValues(r.add);
    const pdVals = rangeValues(r.pd);
    const pdEyeVals = rangeValues(r.pdSingleEye);

    const rxRow = (eye, eyeLabel) => `
      <tr>
        <th scope="row">${escapeHtml(eyeLabel)}</th>
        <td><select data-rx-input="${eye}:sph" aria-label="${escapeHtml(eyeLabel)} sphere (SPH)">${optsHtml(sphereVals, sphFmt, this.state.rx[eye].sph)}</select></td>
        <td><select data-rx-input="${eye}:cyl" aria-label="${escapeHtml(eyeLabel)} cylinder (CYL)">${optsHtml(cylinderVals, cylFmt, this.state.rx[eye].cyl)}</select></td>
        <td><select data-rx-input="${eye}:axis" aria-label="${escapeHtml(eyeLabel)} axis">${optsHtml(axisVals, axFmt, this.state.rx[eye].axis)}</select></td>
        ${showAdd ? `<td><select data-rx-input="${eye}:add" aria-label="${escapeHtml(eyeLabel)} add power (ADD)">${optsHtml(addVals, addFmt, this.state.rx[eye].add)}</select></td>` : ''}
      </tr>`;

    return `
      <p class="lens-selector__rx-helper">Have your prescription handy? Enter the values below. Don't have it on hand? <button type="button" class="lens-selector__link" data-rx-send-later>I'll send my prescription separately</button>.</p>
      <div class="lens-selector__rx-table">
        <table>
          <thead><tr><th scope="col"><span class="lens-selector__visually-hidden">Eye</span></th><th scope="col">SPH</th><th scope="col">CYL</th><th scope="col">AXIS</th>${showAdd ? '<th scope="col">ADD</th>' : ''}</tr></thead>
          <tbody>
            ${rxRow('od', 'OD (right)')}
            ${rxRow('os', 'OS (left)')}
          </tbody>
        </table>
      </div>
      <div class="lens-selector__pd-row">
        <label class="lens-selector__pd-label">PD <span class="lens-selector__pd-required" aria-label="required">*</span></label>
        ${dualMode
          ? `<label>R <select data-rx-input="pd:rightPd" aria-label="Right pupillary distance (PD)">${optsHtml(pdEyeVals, pdFmt, pd.rightPd)}</select></label>
             <label>L <select data-rx-input="pd:leftPd" aria-label="Left pupillary distance (PD)">${optsHtml(pdEyeVals, pdFmt, pd.leftPd)}</select></label>`
          : `<select data-rx-input="pd:single" aria-label="Pupillary distance (PD)">${optsHtml(pdVals, pdFmt, pd.single)}</select>`
        }
        <label class="lens-selector__pd-toggle"><input type="checkbox" data-pd-mode ${dualMode ? 'checked' : ''}> I have 2 PD numbers</label>
      </div>`;
  }

  renderStepCoatings() {
    const c = this.data.coatings;
    const cur = this.state.coatings;
    const sunCard = (id) => {
      const co = c[id];
      const checked = cur.sunglass === id;
      const colorPicker = checked && co.colors ? `
        <div class="lens-selector__color-picker" role="radiogroup" aria-label="Color">
          ${co.colors.map(col => `
            <label class="lens-selector__color-option">
              <input type="radio" name="lens-color-${escapeHtml(id)}" value="${escapeHtml(col)}" data-coating-color ${cur.sunglassColor === col ? 'checked' : ''}>
              <span>${escapeHtml(capitalize(col))}</span>
            </label>`).join('')}
        </div>` : '';
      return `
        <label class="lens-selector__coating-card${checked ? ' is-selected' : ''}">
          <input type="checkbox" data-coating-sun value="${escapeHtml(id)}" ${checked ? 'checked' : ''}>
          <span class="lens-selector__coating-info">
            <span class="lens-selector__coating-label">${escapeHtml(co.label)}</span>
            <span class="lens-selector__coating-desc">${escapeHtml(co.description)}</span>
          </span>
          <span class="lens-selector__coating-price">+$${escapeHtml(String(co.price))}</span>
          ${colorPicker}
        </label>`;
    };
    return `
      <p class="lens-selector__free-banner">All lenses include <strong>scratch-resistant</strong> and <strong>anti-reflective</strong> coatings at no extra charge.</p>
      <label class="lens-selector__coating-card${cur.blueLight ? ' is-selected' : ''}">
        <input type="checkbox" data-coating-blue ${cur.blueLight ? 'checked' : ''}>
        <span class="lens-selector__coating-info">
          <span class="lens-selector__coating-label">${escapeHtml(c.blueLight.label)}</span>
          <span class="lens-selector__coating-desc">${escapeHtml(c.blueLight.description)}</span>
        </span>
        <span class="lens-selector__coating-price">+$${escapeHtml(String(c.blueLight.price))}</span>
      </label>
      <p class="lens-selector__sunglass-hint">Pick up to one sunglass coating below:</p>
      ${['polarized', 'tint', 'transition', 'gradient'].map(sunCard).join('')}`;
  }

  renderStepMaterial() {
    const avail = this.data.basePrice[this.state.type] || {};
    return this.data.materials.map(m => {
      const price = avail[m.id];
      if (price === undefined) return '';
      const selected = this.state.material === m.id ? ' is-selected' : '';
      return `
        <button type="button" class="lens-selector__card lens-selector__material-card${selected}" data-material="${escapeHtml(m.id)}">
          <h3 class="lens-selector__card-title">${escapeHtml(m.label)}</h3>
          <p class="lens-selector__card-desc">${escapeHtml(m.description)}</p>
          <span class="lens-selector__card-price">$${escapeHtml(String(price))}</span>
        </button>`;
    }).join('');
  }

  renderStepReview() {
    const typeObj = this.data.lensTypes.find(t => t.id === this.state.type);
    const matObj = this.data.materials.find(m => m.id === this.state.material);
    const lensPrice = computeLensPrice(this.state, this.data);
    const frameDollars = this.framePrice / 100;
    const total = frameDollars + lensPrice;
    const matPrice = this.data.basePrice[this.state.type]?.[this.state.material];

    const rxBlock = this.state.rx.sendLater
      ? `<span class="lens-selector__pill">We'll request your prescription by email after you order</span>`
      : `<small>${escapeHtml(describeRxLine('OD', this.state.rx.od, typeObj?.requiresAdd))}<br>${escapeHtml(describeRxLine('OS', this.state.rx.os, typeObj?.requiresAdd))}<br>PD: ${escapeHtml(describePD(this.state.rx.pd))}</small>`;

    return `
      <dl class="lens-selector__review">
        <div><dt>Frame</dt><dd>${escapeHtml(this.frameTitle)} <span class="lens-selector__review-price">$${frameDollars.toFixed(2)}</span></dd></div>
        <div><dt>Prescription type</dt><dd>${escapeHtml(typeObj?.label || '-')} <a href="#" data-jump-to="1" class="lens-selector__edit">Edit</a></dd></div>
        <div><dt>Lens material</dt><dd>${escapeHtml(matObj?.label || '-')} ${matPrice ? `<span class="lens-selector__review-price">+$${escapeHtml(String(matPrice))}</span>` : ''} <a href="#" data-jump-to="4" class="lens-selector__edit">Edit</a></dd></div>
        <div><dt>Coatings</dt><dd>${escapeHtml(describeCoatings(this.state.coatings, this.data))} <a href="#" data-jump-to="3" class="lens-selector__edit">Edit</a></dd></div>
        <div><dt>Prescription</dt><dd>${rxBlock} <a href="#" data-jump-to="2" class="lens-selector__edit">Edit</a></dd></div>
      </dl>
      <label class="lens-selector__patient-row">
        <span>Patient name (optional)</span>
        <input type="text" data-patient-name value="${escapeHtml(this.state.patientName)}" placeholder="Whose glasses are these?">
      </label>
      <div class="lens-selector__total">
        <span>Total</span>
        <strong>$${total.toFixed(2)}</strong>
      </div>`;
  }

  // ----------------- PD-missing rescue modal -----------------

  openRxRescueModal() {
    const noSph = !this.state.rx.od.sph || !this.state.rx.os.sph;
    const pd = this.state.rx.pd;
    const noPd = pd.mode === 'single' ? !pd.single : (!pd.rightPd || !pd.leftPd);
    let title, body;
    if (noSph && noPd) {
      title = 'Your prescription is incomplete';
      body = "We need the sphere (SPH) for both eyes and your PD to make your lenses. Add them to continue, or skip and we'll request your full prescription before processing your order.";
    } else if (noSph) {
      title = 'Your prescription is incomplete';
      body = "Please add the sphere (SPH) value for both eyes; it's required to make your lenses. Or skip and we'll request your full prescription before processing your order.";
    } else {
      title = 'Your PD is missing';
      body = "An accurate PD helps your new lenses align correctly with your eyes. Enter it now to continue, or skip and we'll reach out before processing your order.";
    }
    const modal = document.createElement('dialog');
    modal.className = 'lens-selector__rescue';
    setBody(modal, `
      <div class="lens-selector__rescue-body">
        <h3>${escapeHtml(title)}</h3>
        <p>${escapeHtml(body)}</p>
        <button type="button" class="lens-selector__btn-primary" data-rescue-enter>Complete it now</button>
        <button type="button" class="lens-selector__link" data-rescue-skip>Skip and send later</button>
      </div>`);
    this.dialog.appendChild(modal);
    modal.showModal();
    modal.querySelector('[data-rescue-enter]').addEventListener('click', () => { modal.close(); modal.remove(); });
    modal.querySelector('[data-rescue-skip]').addEventListener('click', () => {
      this.state.rx.sendLater = true; modal.close(); modal.remove(); this.goToStep(3);
    });
  }

  // ----------------- add-to-cart -----------------

  async handleAddToCart() {
    const lensPrice = computeLensPrice(this.state, this.data);
    const rounded = roundUpToBracket(lensPrice, this.data.addOnBracketSize);
    const variantData = this.addon.variants?.[String(rounded)];
    if (!variantData) {
      console.error('[lens-selector] No variant for price', rounded);
      alert(`Sorry, we couldn't find a matching lens add-on for that combination. Please try a different selection.`);
      return;
    }
    const lensVariantId = gidToNumericId(variantData.id);
    const lensSelectorId = uuid();
    const typeObj = this.data.lensTypes.find(t => t.id === this.state.type);
    const matObj = this.data.materials.find(m => m.id === this.state.material);

    const frameProps = {
      '_lens_selector_id': lensSelectorId,
      '_lens_selector_version': '1',
      'Usage': typeObj?.label || '',
      'Lens Material': matObj?.label || '',
      'Coatings': describeCoatings(this.state.coatings, this.data),
      'Right Eye (OD)': describeRxLine('OD', this.state.rx.od, typeObj?.requiresAdd).replace(/^OD: /, ''),
      'Left Eye (OS)': describeRxLine('OS', this.state.rx.os, typeObj?.requiresAdd).replace(/^OS: /, ''),
      'PD': describePD(this.state.rx.pd),
      'Prescription': this.state.rx.sendLater ? 'Will send later' : 'Provided in admin',
      'Lens add-on': `$${rounded} CAD`,
    };
    if (this.state.patientName) frameProps['Patient name'] = this.state.patientName;

    const lensProps = {
      '_lens_selector_id': lensSelectorId,
      'For frame': this.frameTitle,
    };

    try {
      await this.postToCart([
        { id: this.frameVariantId, quantity: 1, properties: frameProps },
        { id: lensVariantId, quantity: 1, properties: lensProps },
      ]);
      this.handleClose();
      this.openCartDrawer();
    } catch (err) {
      console.error('[lens-selector] cart add failed', err);
      alert(`Sorry, we couldn't add this to your cart. Please try again.`);
    }
  }

  async postToCart(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) throw new Error(`cart/add: ${res.status}`);
    const addedItems = await res.json();
    // Notify Atelier's cart-items component to re-fetch its section. Without
    // this the cart drawer (and any cart-icon badge) reads stale state.
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: addedItems,
        sourceId: 'lens-selector',
        data: { source: 'lens-selector' },
      },
    }));
    return addedItems;
  }

  openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component, [data-cart-drawer]');
    if (drawer && typeof drawer.open === 'function') drawer.open();
    else window.location.href = '/cart';
  }
}

if (!customElements.get('lens-selector-component')) {
  customElements.define('lens-selector-component', LensSelectorComponent);
}
